import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PageHeader, CapabilityChip, BotAvatar, AccountHealthChip, EmptyState } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Loader2, ShieldCheck, KeyRound, Rocket, UserPlus, Users2, Info, CheckCircle2, AlertCircle } from "lucide-react";

const PURPOSES = ["General", "AI Assistant", "Support Bot", "Community Bot", "Channel Bot", "Business Bot", "Mini App Bot", "Notification Bot", "Custom"];
const TEMPLATES = ["Blank", "AI Assistant", "Support", "FAQ", "Lead Generation", "Notification", "Moderation", "Custom"];

// Mirrors account_routes.CREATE_STAGES; the label is what the operator sees.
const STAGES = [
  ["VALIDATING_USERNAME", "Validating username"],
  ["CREATING_TELEGRAM_BOT", "Creating the bot in Telegram"],
  ["WAITING_FOR_MANAGER_UPDATE", "Waiting for the Manager Bot"],
  ["CAPTURING_TOKEN", "Capturing the token"],
  ["ENCRYPTING_TOKEN", "Encrypting credentials"],
  ["APPLYING_TEMPLATE", "Applying template"],
  ["CONFIGURING_WEBHOOK", "Configuring delivery"],
  ["VERIFYING", "Verifying"],
  ["COMPLETED", "Ready"],
];

function CreateProgress({ job }) {
  const idx = Math.max(0, STAGES.findIndex(([s]) => s === job.stage));
  const failed = job.status === "FAILED";
  const err = job.result?.error || job.error;
  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-3" data-testid="create-progress">
      <div className="flex items-center gap-2 mb-1">
        <Rocket size={16} className="text-primary" />
        <h3 className="font-semibold">{failed ? "Creation stopped" : job.status === "COMPLETED" ? "Bot ready" : "Creating your bot…"}</h3>
      </div>
      <div className="space-y-1.5">
        {STAGES.map(([s, label], i) => {
          const done = i < idx || job.status === "COMPLETED";
          const active = i === idx && job.status !== "COMPLETED" && !failed;
          const isFail = failed && i === idx;
          return (
            <div key={s} className={`flex items-center gap-2.5 text-sm ${done ? "text-foreground" : active ? "text-foreground" : "text-muted-foreground/50"}`}>
              {done ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                : isFail ? <AlertCircle size={16} className="text-rose-500 shrink-0" />
                : active ? <Loader2 size={16} className="animate-spin text-primary shrink-0" />
                : <div className="w-4 h-4 rounded-full border border-current/30 shrink-0" />}
              {label}
            </div>
          );
        })}
      </div>
      {failed && <p className="text-sm text-rose-500 mt-2" data-testid="create-error">{err || "Something went wrong."}</p>}
    </div>
  );
}

function AccountCreate({ accounts, cfg }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [accountId, setAccountId] = useState(sp.get("account") || accounts[0]?.id || "");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [purpose, setPurpose] = useState("General");
  const [template, setTemplate] = useState("Blank");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const account = accounts.find((a) => a.id === accountId);

  // Availability is resolved by MTProto bots.checkUsername through the selected
  // account's session, so the answer depends on which account is chosen.
  // Telegram's own guidance is to debounce this call.
  useEffect(() => {
    if (!username) { setCheck(null); return; }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await api.get("/telegram/check-username", { params: { username, account_id: accountId || undefined } });
        setCheck(data);
      } catch (e) { setCheck({ valid: false, available: "invalid", reason: apiErr(e) }); }
      setChecking(false);
    }, 400);
    return () => clearTimeout(t);
  }, [username, accountId]);

  // The active CREATE_MANAGED_BOT job; its stage updates arrive over SSE.
  const [job, setJob] = useState(null);
  const jobIdRef = useRef(null);

  const applyJob = useCallback((j) => {
    if (!j || j.id !== jobIdRef.current) return;
    setJob(j);
    if (j.status === "COMPLETED" && j.result?.bot_id) {
      jobIdRef.current = null;
      toast.success(`@${j.result.username} is ready`, { description: "Token captured & encrypted automatically." });
      navigate(`/bots/${j.result.bot_id}`);
    } else if (j.status === "FAILED") {
      jobIdRef.current = null;
      setBusy(false);
    }
  }, [navigate]);

  useLiveEvents(["job.created", "job.updated"], (e) => applyJob(e.job));

  // Poll fallback in case SSE is disconnected mid-creation. Keyed on the job
  // *state* (not the ref): an effect keyed on `busy` ran before the POST had
  // returned a job id, saw no id, bailed, and never re-ran — so the fallback
  // silently never started and a dropped SSE stream left the checklist frozen
  // on "Validating username" even though the job had completed server-side.
  const activeJobId = job?.id;
  const terminal = job?.status === "COMPLETED" || job?.status === "FAILED";
  useEffect(() => {
    if (!activeJobId || terminal) return;
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/jobs/${activeJobId}`); applyJob(data); } catch { /* keep waiting */ }
    }, 2500);
    return () => clearInterval(t);
  }, [activeJobId, terminal, applyJob]);

  const create = async () => {
    setBusy(true);
    setJob(null);
    try {
      const { data } = await api.post(`/telegram-accounts/${accountId}/create-bot`, { name, username, purpose, template });
      jobIdRef.current = data.job_id;
      setJob({ id: data.job_id, stage: "VALIDATING_USERNAME", status: "RUNNING" });
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  // Never let "unknown" through: bots.createBot would fail with USERNAME_OCCUPIED
  // after the user has already committed a name.
  const usernameOk = check?.available === "available";

  if (job) {
    return (
      <div className="max-w-xl space-y-4">
        <CreateProgress job={job} />
        {job.status === "FAILED" && (
          <Button variant="outline" onClick={() => { setJob(null); setBusy(false); }} data-testid="btn-create-retry">
            Back to form
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      {!cfg.manager_configured && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 rounded-lg p-3 text-sm" data-testid="manager-notice">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>A platform Manager Bot isn’t configured yet, so new-bot creation is disabled. You can still import an existing token from the Advanced tab.</span>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div><Label>Owner Telegram account</Label>
          <Select value={accountId} onValueChange={setAccountId}><SelectTrigger data-testid="select-owner-account"><SelectValue /></SelectTrigger>
            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name || a.username || a.phone_masked}{a.username ? ` · @${a.username}` : ""}</SelectItem>)}</SelectContent></Select>
        </div>
        {account && (
          <div className="flex items-center gap-3 bg-secondary/40 rounded-lg p-3">
            <BotAvatar name={account.first_name} username={account.username} size={40} />
            <div className="min-w-0 flex-1"><div className="font-medium text-sm truncate">{[account.first_name, account.last_name].filter(Boolean).join(" ") || account.username}</div>
              <div className="text-xs text-muted-foreground mono truncate">{account.username ? "@" + account.username : account.phone_masked}</div></div>
            <AccountHealthChip status={account.status} />
          </div>
        )}
        <div className="flex items-center gap-2"><h3 className="font-semibold">New bot</h3><CapabilityChip chip="Managed" /></div>
        <div><Label>Display name</Label><Input data-testid="input-bot-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="AI Assistant" /></div>
        <div><Label>Username</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground mono text-sm">@</span>
            <Input data-testid="input-bot-username" className="pl-7 mono" value={username} onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))} placeholder="my_assistant_bot" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {checking ? <Loader2 size={15} className="animate-spin text-muted-foreground" /> :
                check?.available === "available" ? <Check size={15} className="text-emerald-500" /> :
                check?.available === "taken" || check?.available === "invalid" ? <X size={15} className="text-rose-500" /> : null}
            </span>
          </div>
          {check && (
            <p data-testid="username-check-msg" className={`text-xs mt-1 ${check.available === "taken" || check.available === "invalid" ? "text-rose-500" : check.available === "available" ? "text-emerald-500" : "text-muted-foreground"}`}>
              {check.reason}
              {check.checked_with === "mtproto" && check.account_username ? ` (via @${check.account_username})` : ""}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Purpose</Label><Select value={purpose} onValueChange={setPurpose}><SelectTrigger data-testid="select-purpose"><SelectValue /></SelectTrigger><SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Template</Label><Select value={template} onValueChange={setTemplate}><SelectTrigger data-testid="select-template"><SelectValue /></SelectTrigger><SelectContent>{TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <Button onClick={create} disabled={busy || !name || !username || !accountId || !(check && usernameOk) || !cfg.manager_configured} data-testid="btn-create-managed" className="gap-1.5">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Create Bot
        </Button>
        <p className="text-xs text-muted-foreground">No BotFather, no token copying — BotControl creates the bot on this account and captures &amp; encrypts its token automatically.</p>
      </div>
    </div>
  );
}

function ImportBot() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bots/import", { token });
      toast.success(`Imported @${data.username}`, { description: "Token encrypted at rest." });
      navigate(`/bots/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-xl space-y-5">
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2"><h3 className="font-semibold">Import existing bot (advanced)</h3><CapabilityChip chip="Direct" /></div>
        <p className="text-sm text-muted-foreground">Paste a bot token from @BotFather. We validate it with <span className="mono">getMe</span>, encrypt it, then discard the plaintext.</p>
        <div><Label className="flex items-center gap-1.5"><KeyRound size={14} /> Bot token</Label><Input data-testid="input-import-token" className="mono" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456789:AAE…" /></div>
        <Button onClick={submit} disabled={busy || !token} data-testid="btn-import-bot" className="gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Validate &amp; import</Button>
      </div>
    </div>
  );
}

export default function CreateBot() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);
  const [cfg, setCfg] = useState({ manager_configured: false });
  useEffect(() => {
    api.get("/telegram-accounts").then((r) => setAccounts(r.data.items)).catch(() => setAccounts([]));
    api.get("/config/telegram").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <PageHeader title="Create Bot" desc="New bots are created from a connected Telegram account — no token copying." testId="create-header" />
      {accounts === null ? null : accounts.length === 0 ? (
        <Tabs defaultValue="connect" className="max-w-xl">
          <TabsList><TabsTrigger value="connect" data-testid="tab-connect">Connect account</TabsTrigger><TabsTrigger value="import" data-testid="tab-import">Import token (advanced)</TabsTrigger></TabsList>
          <TabsContent value="connect" className="mt-5">
            <div className="bg-card border border-border rounded-lg"><EmptyState icon={Users2} title="Connect a Telegram account first"
              desc="Account-first creation lets Telegram create the bot for you and we manage it automatically."
              action={<Button className="gap-1.5" onClick={() => navigate("/accounts/add")} data-testid="btn-goto-add-account"><UserPlus size={15} /> Connect Telegram Account</Button>} testId="empty-create-accounts" /></div>
          </TabsContent>
          <TabsContent value="import" className="mt-5"><ImportBot /></TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="managed" className="max-w-xl">
          <TabsList data-testid="create-mode-tabs">
            <TabsTrigger value="managed" data-testid="tab-managed">Create from account</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">Import token (advanced)</TabsTrigger>
          </TabsList>
          <TabsContent value="managed" className="mt-5"><AccountCreate accounts={accounts} cfg={cfg} /></TabsContent>
          <TabsContent value="import" className="mt-5"><ImportBot /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
